import { create } from 'zustand';
import type { AppPlan } from '@/webapp/plan/planTypes';
import type { StyleBrief } from '@/llm/planTypes';
import type { AttachedFile } from '../api/media';
import type { PlanResult } from '../api/build';

/**
 * A document the app is built AROUND — held here only once it is complete.
 *
 * The raw text lives in memory and is never persisted: a book is large, and a blob URL is
 * meaningless the moment the page reloads. What survives a publish is the MARKER on the
 * definition — what the text is called and where it came from.
 */
export interface AttachedDoc {
  title: string;
  attribution: string;
  text: string;
  headingPattern: string;
}

/**
 * What someone has said about the app they want, before it exists.
 *
 * Replaces the shared onboardingStore, which carries a dozen fields the web never uses and
 * pulls the mobile storage layer in with it. This holds exactly what the three /new screens
 * write and the build reads, and nothing else.
 *
 * Deliberately NOT persisted. A half-described app is not worth restoring across sessions,
 * and a stale plan reappearing under a new idea is the bug that made the phone's flow
 * confusing — which is why the idea screen clears the plan on every change.
 *
 * Pressing Build is where that stops being true: from then the choices are something the
 * server is charging for, so the build writes them down — a reserved project row and a
 * persisted intent (state/buildIntent.ts) — before it asks. The draft stays memory.
 */
export interface Draft {
  intent: 'build' | 'browse' | null;
  kind: 'personal' | 'sharable';
  description: string;
  appName: string;
  vibe: string | null;
  plan: AppPlan | null;
  /**
   * The plan the server tailored, and the idea it tailored it for.
   *
   * `/plan` is charged before it answers, so a plan screen that mounts again for the same
   * idea — back from signing in, the browser's back and forward, or Continue pressed again on
   * the idea screen — must not buy the same tailoring twice, nor lay a fresh one over choices
   * the person has made since.
   *
   * Kept apart from `plan`, which is the person's working copy and which the idea screen
   * clears on every Continue: held there, the paid result went with it.
   */
  tailored: { idea: string; plan: AppPlan } | null;
  /**
   * The /plan call in flight, if any, so a screen mounted while it runs waits for THAT answer
   * instead of asking (and paying) again. Memory only, like the rest of the draft.
   */
  tailoring: { idea: string; result: Promise<PlanResult> } | null;
  /** The plan step to reopen on, when the person left it for sign-in or sign-up. */
  planStep: string | null;
  /** Where to resume after a sign-up the build demanded. */
  pendingIntent: 'build' | 'browse' | null;
  /** Only ever set once it has a title AND an attribution — see `documentReady`. */
  attachedDoc: AttachedDoc | null;
  /** Pictures, clips and sounds the app will be written to use BY NAME. */
  attachedMedia: AttachedFile[];
  /** The feel of a reference picture. Never its palette — the kit owns colour. */
  styleBrief: StyleBrief | null;

  setIntent: (intent: 'build' | 'browse') => void;
  setKind: (kind: 'personal' | 'sharable') => void;
  setDescription: (description: string) => void;
  setAppName: (appName: string) => void;
  setVibe: (vibe: string | null) => void;
  setPlan: (plan: AppPlan | null) => void;
  setTailored: (tailored: { idea: string; plan: AppPlan } | null) => void;
  setTailoring: (tailoring: { idea: string; result: Promise<PlanResult> } | null) => void;
  setPlanStep: (step: string | null) => void;
  setPendingIntent: (intent: 'build' | 'browse' | null) => void;
  setAttachedDoc: (doc: AttachedDoc | null) => void;
  setAttachedMedia: (media: AttachedFile[]) => void;
  setStyleBrief: (brief: StyleBrief | null) => void;
  reset: () => void;
}

const EMPTY = {
  intent: null,
  kind: 'personal',
  description: '',
  appName: '',
  vibe: null,
  plan: null,
  tailored: null,
  tailoring: null,
  planStep: null,
  pendingIntent: null,
  attachedDoc: null,
  attachedMedia: [],
  styleBrief: null,
} satisfies Omit<
  Draft,
  | 'setIntent' | 'setKind' | 'setDescription' | 'setAppName' | 'setVibe' | 'setPlan'
  | 'setTailored' | 'setTailoring' | 'setPlanStep'
  | 'setPendingIntent' | 'setAttachedDoc' | 'setAttachedMedia' | 'setStyleBrief' | 'reset'
>;

export const useDraft = create<Draft>((set) => ({
  ...EMPTY,
  setIntent: (intent) => set({ intent }),
  setKind: (kind) => set({ kind }),
  setDescription: (description) => set({ description }),
  setAppName: (appName) => set({ appName }),
  setVibe: (vibe) => set({ vibe }),
  setPlan: (plan) => set({ plan }),
  setTailored: (tailored) => set({ tailored }),
  setTailoring: (tailoring) => set({ tailoring }),
  setPlanStep: (planStep) => set({ planStep }),
  setPendingIntent: (pendingIntent) => set({ pendingIntent }),
  setAttachedDoc: (attachedDoc) => set({ attachedDoc }),
  setAttachedMedia: (attachedMedia) => set({ attachedMedia }),
  setStyleBrief: (styleBrief) => set({ styleBrief }),
  reset: () => set(EMPTY),
}));
